import {createElement, Fragment, type ReactNode} from "react";
import {sortBy} from "es-toolkit/compat";
import type {Project, ProjectId} from "../cdx-models/Project";
import type {User} from "../cdx-models/User";
import type {Account, AccountId} from "../cdx-models/Account";
import type {ProjectTag} from "../cdx-models/ProjectTag";
import type {UserTag} from "../cdx-models/UserTag";
import type {Root} from "../cdx-models/Root";

export function debounce<T extends (...args: any[]) => any>(fn: T, ms: number) {
  let id: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<T>): Promise<ReturnType<T>> => {
    if (id) clearTimeout(id);
    return new Promise((resolve) => {
      id = setTimeout(() => resolve(fn(...args)), ms);
    });
  };

  debounced.cancel = () => {
    if (id) clearTimeout(id);
    id = null;
  };

  return debounced;
}

export function formatAsEur(amount: number) {
  return `€${(amount / 100).toFixed(2)}`;
}

const emptyArr: [] = [];

export function unsortedProjectTags(project: Project) {
  return project.tags.length > 0 && project.tags[0].$meta.isFieldPresent("tag", true)
    ? project.tags
    : emptyArr;
}

export type TagObject =
  | {type: "personal"; tagObject: UserTag}
  | {type: "project"; tagObject: ProjectTag};

export function unsortedPersonalTags(user: User | null, account: Account) {
  const personalTags2 =
    account.$meta.isLoaded && user
      ? user.$meta.find("tags", {accountId: account.id as AccountId})
      : emptyArr;
  return personalTags2.length > 0 && personalTags2[0].$meta.isFieldPresent("tag", true)
    ? personalTags2
    : emptyArr;
}

export function tagObjectsForUser(
  user: User | null,
  projects: Project[],
  account: Account
): TagObject[] {
  const tags: TagObject[] = unsortedPersonalTags(user, account).map(
    (t) => ({type: "personal", tagObject: t}) satisfies TagObject
  );
  projects.forEach((project) =>
    tags.push(
      ...unsortedProjectTags(project).map(
        (t) => ({type: "project", tagObject: t}) satisfies TagObject
      )
    )
  );
  return sortBy(tags, (tO) => tO.tagObject.tag.toLowerCase());
}

export function personalTags(user: User | null, account: Account) {
  return sortBy(unsortedPersonalTags(user, account), (t: UserTag) => t.tag.toLowerCase());
}

export function projectTags(project: Project) {
  return sortBy(unsortedProjectTags(project), (t) => t.tag.toLowerCase());
}

export function projectsTags(projects: Project[]) {
  const list = projects.reduce((l, p) => {
    l.push(...unsortedProjectTags(p));
    return l;
  }, [] as ProjectTag[]);
  return sortBy(list, (t) => t.tag.toLowerCase());
}

export function accountRoles(account: Account, {allowObservers}: {allowObservers?: boolean} = {}) {
  return account.$meta.find("roles", {
    role: allowObservers ? ["owner", "admin", "guest", "observer"] : ["owner", "admin", "guest"],
  });
}

export function accountUsers(account: Account, {allowObservers}: {allowObservers?: boolean} = {}) {
  return accountRoles(account, {allowObservers}).map((r) => r.user);
}

export function projectsUsers(
  account: Account,
  projects: Project[],
  {allowObservers}: {allowObservers?: boolean} = {}
) {
  const projectIds = projects.map((p) => p.$meta.get("id", null)).filter(Boolean);
  return account.$meta
    .find("roles", {
      $or: [
        {role: ["admin", "owner"]},
        {
          role: allowObservers ? ["guest", "observer"] : "guest",
          user: {withProjectAccess: {projectId: projectIds}},
        },
      ],
    })
    .map((r) => r.user);
}

export function projectDecks(p: Project) {
  return p.$meta.find("decks", {isDeleted: false});
}

export function sortedProjectDecks(p: Project) {
  return p.$meta.find("decks", {isDeleted: false, $order: ["sortValue", "id"]});
}

export function projectsDecks({root, projectIds}: {root: Root; projectIds: ProjectId[]}) {
  return root.account.$meta.find("anyDecks", {projectId: projectIds, isDeleted: false});
}

const MOD_ADLER = 65521;

export function adler32(str: string) {
  let a = 1,
    b = 0;
  for (let i = 0, len = str.length; i < len; ++i) {
    a = (a + str.charCodeAt(i)) % MOD_ADLER;
    b = (b + a) % MOD_ADLER;
  }
  return (b << 16) | a;
}

export const joinComponents = (comps: ReactNode[], joiner: ReactNode) => {
  const list: ReactNode[] = [];
  comps.forEach((c, i) => {
    list.push(c);
    if (i + 1 < comps.length) list.push(joiner);
  });
  return createElement(Fragment, {}, ...list);
};

export const joinAnd = (list: string[]) => {
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  return [list.slice(0, -1).join(", "), list[list.length - 1]].join(" and ");
};

export const joinAndComponents = (list: ReactNode[]) => {
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  const children: ReactNode[] = [list[0]];
  for (let i = 1; i < list.length - 1; i += 1) {
    children.push(", ", list[i]);
  }
  children.push(" and ", list[list.length - 1]);
  return createElement(Fragment, {}, ...children);
};

export const pluralize = (count: number, word: string, plural?: string) => {
  return `${count} ${count === 1 ? word : plural || `${word}s`}`;
};

export const slugify = (input: string) =>
  (input || "")
    .replace(/[^\p{L}0-9]+/gu, " ")
    .trim()
    .replace(/ /g, "-")
    .toLowerCase();

const specialOrds: Record<number, string> = {11: "th", 12: "th", 13: "th"};
const singleDigitOrds: Record<number, string> = {1: "st", 2: "nd", 3: "rd"};

// convert to ordinal numbers:
export const ordinalSuffix = (num: number) => {
  return specialOrds[num % 100] || singleDigitOrds[num % 10] || "th";
};

export const toOrdinal = (num: number) => {
  return `${num}${ordinalSuffix(num)}`;
};

export const isSelectionActive = () => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    if (!(range.startContainer === range.endContainer && range.startOffset === range.endOffset)) {
      return true;
    }
  }
  return false;
};

export const formatNumber = (
  num: number,
  {maximumFractionDigits = 1}: {maximumFractionDigits?: number} = {}
) => {
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
  });
  return formatter.format(num);
};

// brify
export const joinByBr = (list: ReactNode[]) => {
  const lines: ReactNode[] = [];
  list.forEach((entry) => lines.push(entry, <br />));
  return createElement(Fragment, {}, ...lines.slice(0, -1));
};

export const toBigInt = (str: string | null) => {
  return str === null ? null : window.BigInt ? window.BigInt(str) : Number(str);
};

export const getIndexViaBinarySearch = <TListItem, TEl>(
  list: TListItem[],
  el: TEl,
  cmpFn: (el: TEl, item: TListItem) => number
) => {
  let m = 0;
  let n = list.length - 1;
  while (m <= n) {
    const k = (n + m) >> 1;
    const cmp = cmpFn(el, list[k]);
    if (cmp > 0) {
      m = k + 1;
    } else if (cmp < 0) {
      n = k - 1;
    } else {
      return k;
    }
  }
  return m;
};
